var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import i18n from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isPreview } from '@/config/env';
import { useToast } from '@/hooks';
import { useAuth } from '@/store';
import { APIHeaderKey, HTTP_STATUS } from '@/types';
import { ResolveDate, getLocalStorage, timeZoneName } from '@/utils';
import { AuthService } from '.';
export const instance = axios.create();
const SKIP_PATHS = ['get-partials', 'post-partials', 'start-upload', 'shift'];
const SILENCE_PATHS = {
    GLOBAL: ['event-submission-check'],
    PREVIEW: [
        'get-configurations',
        'post-partials',
        'get-partials',
        'post-submissions',
        'cached-query',
        'event-submission-check',
        'nmible/get-status'
    ],
    STATUS: [{ path: 'nmible/get-status', statuses: [HTTP_STATUS.FORBIDDEN] }]
};
function shouldSkipError(url) {
    return SKIP_PATHS.some((path) => url.includes(path));
}
function shouldSilenceError(url, status) {
    const matchesPreview = isPreview && SILENCE_PATHS.PREVIEW.some((path) => url.includes(path));
    const matchesGlobal = SILENCE_PATHS.GLOBAL.some((path) => url.includes(path));
    const matchesStatus = SILENCE_PATHS.STATUS.some(({ path, statuses }) => url.includes(path) && statuses.includes(status));
    return matchesGlobal || matchesPreview || matchesStatus;
}
instance.interceptors.request.use((config) => {
    const resolveDate = new ResolveDate();
    const { access_token, api_url } = getLocalStorage();
    config.baseURL = api_url;
    // SET HEADERS
    config.headers = Object.assign(Object.assign({}, config.headers), { Authorization: `Bearer ${access_token}`, [APIHeaderKey.ACCEPT_LANGUAGE]: i18n.language, [APIHeaderKey.CONTENT_TYPE]: 'application/json', [APIHeaderKey.DATETIME]: resolveDate.formatDate().tz_offset, [APIHeaderKey.TIMEZONE]: timeZoneName() });
    return config;
}, (err) => Promise.reject(err));
export const AxiosInterceptor = ({ children }) => {
    const [isReady, setIsReady] = useState(false);
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { handleLogout, state: { user } } = useAuth();
    let totalRetry = 0;
    const handleUnauthorizedError = (apiError) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const { config, data } = apiError.response;
        if ((_a = config === null || config === void 0 ? void 0 : config.data) === null || _a === void 0 ? void 0 : _a.includes('password')) {
            showToast({ message: data.message });
            return Promise.reject(apiError.response);
        }
        if (totalRetry === 0) {
            totalRetry += 1;
            try {
                yield AuthService.refreshToken();
                return instance.request(config);
            }
            catch (_b) {
                handleLogout();
                return Promise.reject(apiError.response);
            }
        }
        handleLogout();
        return Promise.reject(apiError.response);
    });
    const handleErrorMessage = (apiError) => {
        const { config, response: { data, status } } = apiError;
        const getErrorMessage = () => {
            var _a;
            const defaultMessage = t('common:message.error.default');
            const endpointErrors = [
                {
                    messages: {
                        [HTTP_STATUS.NOT_FOUND]: t('common:message.error.invalidCode'),
                        [HTTP_STATUS.UNPROCESSABLE_ENTITY]: ''
                    },
                    url: '/api/auth/invitation' // to be fixed under TMP-5131
                }
            ];
            const error = endpointErrors.find((x) => config.url.startsWith(x.url) && status in x.messages);
            if (error)
                return error.messages[status] || defaultMessage;
            const errorDetail = (_a = data === null || data === void 0 ? void 0 : data.errors) === null || _a === void 0 ? void 0 : _a.map((x) => x === null || x === void 0 ? void 0 : x.detail);
            if (errorDetail === null || errorDetail === void 0 ? void 0 : errorDetail.length)
                return errorDetail;
            if (data === null || data === void 0 ? void 0 : data.message)
                return data.message;
            if (data === null || data === void 0 ? void 0 : data.error)
                return data.error;
            if (status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
                return defaultMessage;
            }
            return defaultMessage;
        };
        if (shouldSkipError(config.url)) {
            return Promise.resolve(apiError.response);
        }
        const message = getErrorMessage();
        if (message && !shouldSilenceError(config.url, status))
            showToast({ message });
        return Promise.reject(apiError.response);
    };
    useEffect(() => {
        const initialiseInterceptors = () => {
            try {
                const responseInterceptor = (response) => response;
                const responseErrorInterceptor = (err) => {
                    const { status } = err.response;
                    switch (status) {
                        case HTTP_STATUS.UNAUTHORIZED:
                            return handleUnauthorizedError(err);
                        default:
                            return handleErrorMessage(err);
                    }
                };
                const requestInterceptor = (request) => {
                    var _a, _b;
                    const locale = ((_b = (_a = user.data) === null || _a === void 0 ? void 0 : _a.attributes) === null || _b === void 0 ? void 0 : _b.locale) || i18n.language;
                    request.headers[APIHeaderKey.LOCALE] = locale;
                    return request;
                };
                const requestErrorInterceptor = (err) => Promise.reject(err);
                const interceptRequest = instance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
                const interceptResponse = instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor);
                return () => {
                    instance.interceptors.request.eject(interceptRequest);
                    instance.interceptors.response.eject(interceptResponse);
                };
            }
            finally {
                setIsReady(true);
            }
        };
        initialiseInterceptors();
    }, []);
    if (!isReady) {
        return null;
    }
    return children;
};
export default instance;
